import { useEffect } from "react";
import { Row, Col, Card, Image, Carousel, Badge } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "../Components/Loader";
import Message from "../Components/Message";
import { useGetPartCategoriesQuery } from "../slices/productsApiSlice";
import SaleMarqueeRow from "../Components/SaleMarqueeRow";
import Meta from "../Components/Meta";


// Bullet points for the left side of the hero
const HERO_BULLETS = [
   { title: "Premium aftermarket quality", desc: "Built to OEM-level fit and durability." },
   { title: "Direct-from-manufacturer pricing", desc: "No middleman markup." },
   { title: "Fast shipping from Ottawa warehouse", desc: "Quick delivery across Ontario & Canada." },
   { title: "100% vehicle-specific fitment", desc: "Match by make, model, year, and trim." },
   { title: "Warranty-backed brake & filter parts", desc: "Confidence with every purchase." },
 ];

const HIGHLIGHTS = [
  {
    title: "Smart fitment guidance",
    desc: "Match parts by make, model, year, and trim with fewer returns.",
  },
  {
    title: "Live inventory confidence",
    desc: "See the most in-demand categories surfaced right on this page.",
  },
  {
    title: "Fast Canada-wide delivery",
    desc: "Orders move quickly from our Ottawa fulfillment center.",
  },
];

export default function BrowsePartsScreen() {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth || {});
  const isTieredUser = Boolean(userInfo?.tier?.id);
  const [sp] = useSearchParams();
  const make = sp.get("make") || "";
  const model = sp.get("model") || "";
  const year = sp.get("year") || "";

  const { data: cats = [], isLoading, error } = useGetPartCategoriesQuery();

  useEffect(() => {
        const elements = document.querySelectorAll(
      ".browse-reveal:not(.browse-reveal-visible)"
    );
    if (!elements.length) return undefined;

    if (!("IntersectionObserver" in window)) {
      elements.forEach((el) => el.classList.add("browse-reveal-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("browse-reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );

    elements.forEach((el) => observer.observe(el));
    const fallbackTimer = window.setTimeout(() => {
      elements.forEach((el) => el.classList.add("browse-reveal-visible"));
    }, 1500);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallbackTimer);
    };
  }, [cats.length]);

  // Use up to 5 categories as promo slides (image optional)
  const promoSlides = cats.slice(0, 5);

  // From home: user chooses a part first.
  // If vehicle is already known → go straight to results.
  // If not → go to makes screen and carry the chosen category.
  const goNext = (cat) => {
    if (cat.browseMode === "catalog" || cat.key === "engine-oil") {
      navigate(`/${cat.key}`);
      return;
    }

    // we already know vehicle → direct to results
    if (make && model && year) {
      const params = new URLSearchParams({
        make,
        model,
        year,
        category: cat.key,
      });
      navigate(`/results?${params.toString()}`);
      return;
    }

    // no vehicle yet → select make first
    const params = new URLSearchParams({ category: cat.key });
    navigate(`/makes?${params.toString()}`);
  };

  const goToAllProducts = () => {
    navigate("/home");
  };

  return (
    <div className="py-4">
      <Meta canonicalPath="/" />
      {/* Top hero banner */}
      <section className="home-top-hero home-top-hero-full mb-4 browse-hero">
        <div className="hero-top-image-full">
          <Image
            src="/images/kcc-hero.webp"
            srcSet="/images/kcc-hero-800.webp 800w, /images/kcc-hero.webp 1600w"
            sizes="100vw"
            fetchpriority="high"
            alt="KCC Parts Distribution – brake & filter parts"
            fluid
          />
        </div>
        <div className="browse-hero-content">
          <div className="browse-hero-card browse-reveal">
            <p className="browse-hero-eyebrow">KCC Parts Distribution</p>
            <h1 className="browse-hero-title">Find the right parts, fast.</h1>
            <p className="browse-hero-subtitle">
              Choose a category to browse premium brake, filter, and maintenance parts
              tailored to your vehicle.
            </p>
            <div className="browse-hero-actions">
              <a className="btn btn-danger" href="#browse-categories">
                Shop categories
              </a>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={goToAllProducts}
              >
                Browse all products
              </button>
            </div>
            <div className="browse-hero-badges">
              <Badge bg="light" text="dark">Vehicle-specific fitment</Badge>
              <Badge bg="light" text="dark">Ottawa warehouse</Badge>
              <Badge bg="light" text="dark">Warranty-backed</Badge>
            </div>
          </div>
        </div>
      </section>

      <section className="browse-highlight-row mb-4">
        <Row className="g-3">
          {HIGHLIGHTS.map((item, index) => (
            <Col key={item.title} md={4}>
              <Card
                className="border-0 shadow-sm browse-highlight-card browse-reveal"
                style={{ "--reveal-delay": `${0.2 + index * 0.1}s` }}
              >
                <Card.Body>
                  <h5 className="mb-2">{item.title}</h5>
                  <p className="mb-0 text-muted">{item.desc}</p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      {isLoading && <Loader />}
      {error && (
        <Message variant="danger">
          {error?.data?.message || error.error}
        </Message>
      )}

      {!isLoading && !error && cats.length === 0 && (
        <Message>
          No part categories yet.{" "}
          Go to <a href="/admin/part-categories">Admin → Part Categories</a> to
          add some.
        </Message>
      )}

      <section className="mb-4">
        <Card className="border-0 shadow-sm all-products-callout browse-reveal">
          <Card.Body className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
            <div>
              <h2 className="mb-1">All Products</h2>
              <p className="text-muted mb-0">
                Explore our full catalog without selecting a category.
              </p>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={goToAllProducts}
              >
                Browse all products
              </button>
            </div>
          </Card.Body>
        </Card>
      </section>

      {isTieredUser && (
        <section className="mb-4">
          <Card
            className="border-0 shadow-sm bulk-order-callout browse-reveal"
            style={{ "--reveal-delay": "0.1s" }}
          >
            <Card.Body className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
              <div>
                <h2 className="mb-1">Bulk order for partner customers</h2>
                <p className="text-muted mb-0">
                  Search by part number or SKU, enter quantities, and send
                  everything straight to your cart.
                </p>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate("/bulk-order")}
                >
                  Go to bulk ordering
                </button>
              </div>
            </Card.Body>
          </Card>
        </section>
      )}

      <section className="mb-4">
        <Card
          className="border-0 shadow-sm browse-category-section browse-reveal"
          style={{ "--reveal-delay": "0.15s" }}
        >
          <Card.Body className="browse-category-body" id="browse-categories">
            <div className="browse-category-header">
              <div>
                <h2 className="mb-1">Shop by category</h2>
                <p className="text-muted mb-0">
                  Start with the part you need, then match it to your vehicle.
                </p>
              </div>
              <Badge
                bg="danger-subtle"
                text="danger"
                className="browse-category-pill"
              >
                {cats.length} categories
              </Badge>
            </div>

            <Row className="g-3 mt-1 browse-category-grid">
              {cats.map((c, index) => (
                <Col key={c.key} xs={12} sm={6} md={4} lg={3}>
                  <Card
                    role="button"
                    className="part-card h-100 browse-reveal"
                    style={{ "--reveal-delay": `${0.25 + index * 0.05}s` }}
                    onClick={() => goNext(c)}
                  >
                    {/* square layout: image block + title at bottom */}
                    <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                      <div className="part-card-img d-flex align-items-center justify-content-center w-100">
                        {c.image && (
                          <Image
                            src={c.image}
                            alt={c.name}
                            className="img-fluid"
                            onError={(e) =>
                              (e.currentTarget.style.display = "none")
                            }
                          />
                        )}
                      </div>
                      <div className="part-card-title text-center fw-semibold">
                        {c.name}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      </section>

   {promoSlides.length > 0 && (
        <section className="mt-5 home-carousel-hero home-carousel-split browse-reveal">
          {/* LEFT: statements */}
          <div className="hero-left">
            <h3 className="mb-3">Why choose KCC Auto Parts?</h3>
            <ul className="kcc-hero-bullets">
            {HERO_BULLETS.map((b) => (
              <li key={b.title} className="hero-bullet">
                <div className="hero-bullet-title">{b.title}</div>
                <div className="hero-bullet-desc">{b.desc}</div>
              </li>
            ))}
          </ul>
          </div>

          {/* RIGHT: product carousel */}
          <div className="hero-right">
            <Carousel>
              {promoSlides.map((slide) => (
                <Carousel.Item key={slide.key}>
                  <div className="d-flex flex-column align-items-center w-100">
                    {slide.image && (
                      <div className="w-100 d-flex justify-content-center">
                        <Image
                          className="d-block"
                          src={slide.image}
                          alt={slide.name}
                          style={{
                            maxHeight: 350,
                            width: "100%",
                            maxWidth: "650px",
                            objectFit: "contain",
                          }}
                          onError={(e) =>
                            (e.currentTarget.style.display = "none")
                          }
                          fluid
                        />
                      </div>
                    )}

                    <div className="home-carousel-caption text-light mt-4 py-3 px-4 rounded text-center w-100">
                      <h5 className="mb-2">{slide.name}</h5>
                      <p className="mb-0 small">
                        Browse premium {slide.name.toLowerCase()} parts for
                        popular vehicles.
                      </p>
                    </div>
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        </section>
      )}
       <SaleMarqueeRow limit={12} />

      <section className="mt-5">
        <Card className="border-0 shadow-sm p-4 browse-reveal">
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
            <div>
              <h2 className="mb-2">Reliable automotive parts, delivered with care.</h2>
              <p className="text-muted mb-0">
                KCC Parts Distribution is a Canadian supplier dedicated to
                quality and premium aftermarket parts. We support fleet managers, and drivers with fast delivery, fitment
                guidance, and responsive support.
              </p>
            </div>
              <Card className="shadow-sm border-0">
                <Card.Img
                  variant="top"
                  src="warehouse.png"
                  alt="KCC Parts Distribution warehouse and fulfillment"
                />
            </Card>
          </div>

          <Row className="g-4">
            <Col md={4}>
              <Card className="border-0 shadow-sm bulk-order-callout browse-reveal" style={{ "--reveal-delay": "0.1s" }}>
                <Card.Body>
                  <h5>What we do</h5>
                  <p className="mb-0">
                    We stock and source high-quality brake, suspension,
                    filtration, and engine components for most makes and models.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm bulk-order-callout browse-reveal" style={{ "--reveal-delay": "0.2s" }}>
                <Card.Body>
                  <h5>How we support you</h5>
                  <p className="mb-0">
                    Our specialists verify fitment, provide quick quotes, and
                    keep you informed from checkout to delivery.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm bulk-order-callout browse-reveal" style={{ "--reveal-delay": "0.3s" }}>
                <Card.Body>
                  <h5>Why it matters</h5>
                  <p className="mb-0">
                    Accurate parts and dependable logistics mean fewer returns,
                    faster installs, and happier customers.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card>
      </section>
    </div>
  );
}
